import Button from '@/components/button/Button';
import { AccountTierType } from '@/proxies/UserProxy';
import { isNil } from 'lodash-es';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TierCardFeatureLine from './TierCardFeatureLine';
import type { TierFeatureType } from './TiersSection';

interface TierCardProps {
  tier: AccountTierType;
  selected?: boolean;
  popular?: boolean;
  display_monthly_price?: boolean;
  monthly_price: number;
  yearly_price: number;
  currency: string;
  features: TierFeatureType[];
  userTier?: AccountTierType;
  onPlanClick: (plan: AccountTierType) => void;
}

const TierCard: React.FC<TierCardProps> = ({
  tier,
  selected,
  popular,
  monthly_price,
  display_monthly_price,
  yearly_price,
  currency,
  features,
  userTier,
  onPlanClick,
}) => {
  const { t, i18n } = useTranslation();

  const planName = useMemo<string>(() => {
    return t(`static.generic.account_tier_name_${tier.valueOf()}`);
  }, [tier, t]);

  return (
    <div
      className={`${selected ? 'bg-sky-600 border-sky-600 text-slate-200' : 'bg-white border-slate-200 text-slate-500'} relative min-h-[460px] h-full min-w-full max-w-full lg:min-w-[320px] lg:max-w-[320px] rounded-xl border p-4 drop-shadow-sm flex flex-col items-start justify-start gap-1`}
    >
      <span
        className={`absolute top-0 right-0 p-1 rounded-full bg-emerald-500 text-sm font-bold px-3 text-white drop-shadow-sm mr-3 -mt-3 ${popular ? 'block' : 'hidden'}`}
      >
        {t('static.prices.most_popular_label')}
      </span>
      <span
        className={`${selected ? 'text-white' : 'text-slate-800'} font-bold min-w-full text-lg`}
      >
        {planName}
      </span>
      <span className="flex flex-row items-end justify-start font-bold min-w-full text-lg gap-2">
        <span
          className={`${selected ? 'text-white' : 'text-slate-800'} font-bold text-4xl`}
        >
          {new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'narrowSymbol',
          }).format(
            display_monthly_price === true ? monthly_price : yearly_price,
          )}
        </span>
        <span className="font-normal">
          {t(
            tier !== AccountTierType.Free
              ? 'static.prices.paid_suffix'
              : 'static.prices.free_suffix',
          )}
        </span>
      </span>
      <span
        className={`min-w-full pt-2 ${selected ? 'text-white' : 'text-slate-800'}`}
      >
        {t(`static.prices.account_tier_card_description_${tier.valueOf()}`)}
      </span>
      <span className="min-w-full min-h-20 flex flex-col items-center justify-center">
        <span className="min-w-full">
          {isNil(userTier) || tier.valueOf() > userTier.valueOf() ? (
            <Button
              disabled={false}
              loading={false}
              label={t(
                tier !== AccountTierType.Free
                  ? !isNil(userTier)
                    ? 'static.prices.upgrade_btn_label'
                    : 'static.prices.start_free_trial_btn_label'
                  : 'static.prices.sign_up_btn_label',
                { tier_name: planName },
              )}
              onClick={() => onPlanClick(tier)}
              data-test-id={`purchase-plan-click-tier-${tier.valueOf()}`}
              id={`purchase-plan-click-tier-${tier.valueOf()}`}
              custom={selected === true ? 'tier-selected' : 'tier-unselected'}
              small
            />
          ) : tier.valueOf() < userTier.valueOf() ? (
            <span className="min-w-full text-center font-bold flex flex-col items-center justify-center">
              {t('static.prices.included_label')}
            </span>
          ) : (
            <span
              className={`min-w-full text-center font-bold flex flex-col items-center text-lg justify-center ${selected ? 'text-white' : 'text-slate-800'}`}
            >
              {t('static.prices.your_plan_label')}
            </span>
          )}
        </span>
      </span>
      <span
        className={`${selected ? 'text-white' : 'text-slate-800'} font-semibold min-w-full text-lg`}
      >
        {t('static.prices.features_section_title')}
      </span>
      <span
        className={`min-w-full flex flex-col items-center justify-center ${selected ? 'text-white' : 'text-slate-800'}`}
      >
        {features.map((feature) => (
          <TierCardFeatureLine
            key={feature.id}
            id={feature.id}
            tier={tier}
            included={feature.included}
            highlight={feature.highlight}
            selected={feature.selected}
          />
        ))}
      </span>
    </div>
  );
};

export default TierCard;
